// Sugalarm — auth bootstrap.
//
// On mount: if a stored token exists (JWT or device), call /api/auth/me
// to hydrate the current user. While that's pending, status is 'unknown'
// so the app shows a brief logo-only splash. After hydration the status
// is 'signedIn' or 'signedOut'.

import { useCallback, useEffect, useRef, useState } from "react";

import * as authApi from "../api/auth";
import {
  getBearer,
  getDeviceToken,
  mirrorJwtToNative,
  setJwt,
  setJwtPersistMode,
} from "../lib/auth";
import { cancelPreview } from "../lib/alarmPreview";
import { friendlyAuthError } from "../lib/authErrors";
import { restoreTokensFromNative } from "../lib/authBridge";
import {
  teardownAccount,
  purgeOnOwnerMismatch,
} from "../lib/accountTeardown";
import { retryResult } from "../lib/retryResult";
import type { User } from "../lib/types";

export type AuthStatus = "unknown" | "signedIn" | "signedOut";

export interface AuthState {
  status: AuthStatus;
  user: User | null;
  /** Cycle 190: `remember` is web-only (the "Remember me" checkbox). When
   *  provided it selects the token persistence store (localStorage vs
   *  sessionStorage); mobile/kiosk omit it and stay on localStorage. */
  signIn: (email: string, password: string, remember?: boolean) => Promise<{ ok: true } | { ok: false; message: string }>;
  signUp: (email: string, password: string, displayName: string) => Promise<{ ok: true } | { ok: false; message: string }>;
  /** Cycle 152: adopt a TokenPair obtained by an alternate sign-in flow
   *  (Google / Apple / passkey). Same storage + App Group write as the
   *  password path — one session store, not a fork. */
  adoptSession: (data: authApi.AuthResponse) => void;
  signOut: () => Promise<void>;
  /** Force-refresh the user object (e.g. after CGM connect). */
  refreshUser: () => Promise<void>;
  /** True when the session was authed with a device token (kiosk path). */
  isDeviceSession: boolean;
}

export function useAuth(): AuthState {
  const [status, setStatus] = useState<AuthStatus>("unknown");
  const [user, setUser] = useState<User | null>(null);
  const [isDeviceSession, setIsDeviceSession] = useState<boolean>(
    () => getDeviceToken() !== null,
  );

  const statusRef = useRef<AuthStatus>(status);
  statusRef.current = status;

  const hydrate = useCallback(async (signal?: AbortSignal): Promise<void> => {
    if (!getBearer()) {
      // Cycle 144: before declaring signed-out on the native shell, try
      // restoring the session from the iOS App Group container — it
      // holds the JWT pair (written for the Siri intents on every
      // login/refresh) and survives WKWebView storage eviction. Null
      // off-native, on older shells, and after a real sign-out (the
      // container is cleared), so this never resurrects a signed-out
      // session and never affects web/kiosk.
      const restored = await restoreTokensFromNative();
      if (restored) {
        setJwt(restored);
      } else {
        setUser(null);
        setStatus("signedOut");
        setIsDeviceSession(false);
        return;
      }
    } else {
      // Cycle 248: a bearer is already present — the common
      // already-signed-in JWT launch, which skips the restore block above
      // and therefore never ran `setJwt` this launch. Re-mirror the live
      // JWT into the iOS App Group so the Siri App Intents always read the
      // current session (the writer only fired on login/refresh before, so
      // a user who signed in on an earlier build had an empty container and
      // got "sign in first" from Siri forever). No-ops off-native and for a
      // device-token kiosk session (no JWT to mirror).
      mirrorJwtToNative();
    }
    setIsDeviceSession(getDeviceToken() !== null);
    // Cycle 235: retry transient failures (flaky network / 5xx) with
    // backoff instead of treating the first hiccup as "signed out". This is
    // the OTHER half of the daily-logout fix: previously ANY non-ok `me()`
    // here ran `clearAll()`, so a cold launch on a poor connection (expired
    // access token → 401 → refresh can't complete → network error) wiped
    // both localStorage AND the iOS App-Group restore copy. Only a genuine
    // auth rejection (401/403) may tear the session down now.
    const r = await retryResult(() => authApi.me(), { signal });
    if (r.ok) {
      // A RESTORED session is a landing too: this is the path a device takes
      // after a relaunch, and it is the only one a never-completed sign-out
      // can hide behind (the credentials were never cleared, so nothing else
      // ever asks whose data this is).
      void purgeOnOwnerMismatch(r.data.owner_key);
      setUser(r.data);
      setStatus("signedIn");
      return;
    }
    if (r.error.kind === "auth" || r.error.kind === "forbidden") {
      // ⚠ NOT `explicit` — a revoked device token must still land a kiosk on
      // `/pair`, not `/login` (cycle 358). But the registrations have to go
      // either way, or a rejected session keeps its Live Activity alive.
      await teardownAccount({ explicit: false });
      setUser(null);
      setStatus("signedOut");
      setIsDeviceSession(false);
    }
    // else: a transient failure with tokens still in hand — KEEP the
    // session, leave status as-is (the boot splash on first launch, or the
    // prior signedIn), and let the recovery listeners below re-hydrate when
    // connectivity returns. The user is signed in automatically once the
    // network is back, never bounced to the login screen for being offline.
  }, []);

  useEffect(() => {
    const ctrl = new AbortController();
    void hydrate(ctrl.signal);
    // Cycle 235: recover from an offline cold launch. If the first hydrate
    // couldn't reach the server, re-attempt when connectivity returns
    // instead of leaving the user stranded on the splash — and never sign
    // them out for being offline. Skipped once signed in (other surfaces
    // already refresh on focus; re-verifying here would be redundant load).
    const onRecover = (): void => {
      if (document.visibilityState === "hidden") return;
      if (statusRef.current === "signedIn") return;
      void hydrate(ctrl.signal);
    };
    window.addEventListener("online", onRecover);
    document.addEventListener("visibilitychange", onRecover);
    return () => {
      ctrl.abort();
      window.removeEventListener("online", onRecover);
      document.removeEventListener("visibilitychange", onRecover);
    };
  }, [hydrate]);

  const signIn = useCallback<AuthState["signIn"]>(
    async (email, password, remember) => {
      const r = await authApi.login({ email, password });
      if (!r.ok) return { ok: false, message: r.error.message };
      // Cycle 190: web-only "Remember me" — select the token store BEFORE
      // writing the pair. `undefined` (mobile/kiosk) leaves the default
      // (localStorage) untouched.
      if (remember !== undefined) setJwtPersistMode(remember);
      // ⚠ BEFORE `setJwt`. Its synchronous half wipes another account's
      // cached members / settings / readings and records the new owner, so
      // nothing signed-in-shaped is observable while foreign data is still
      // in storage. See `purgeOnOwnerMismatch`.
      void purgeOnOwnerMismatch(r.data.user.owner_key);
      setJwt(r.data.tokens);
      setUser(r.data.user);
      setStatus("signedIn");
      setIsDeviceSession(false);
      return { ok: true };
    },
    [],
  );

  const signUp = useCallback<AuthState["signUp"]>(
    async (email, password, displayName) => {
      const r = await authApi.register({
        email,
        password,
        display_name: displayName,
      });
      if (!r.ok) return { ok: false, message: friendlyAuthError(r.error.message) };
      // A brand-new account cannot COLLIDE, but the device may still be
      // holding the previous one's caches — sign-up is a landing like any
      // other and must clear them before anything renders.
      void purgeOnOwnerMismatch(r.data.user?.owner_key ?? null);
      setJwt(r.data.tokens);
      setUser(r.data.user);
      setStatus("signedIn");
      setIsDeviceSession(false);
      return { ok: true };
    },
    [],
  );

  const adoptSession = useCallback<AuthState["adoptSession"]>((data) => {
    // Same ordering rule as `signIn` — this is the Google / Apple / passkey
    // landing and it must purge a foreign account's data just as hard.
    void purgeOnOwnerMismatch(data.user.owner_key);
    setJwt(data.tokens);
    setUser(data.user);
    setStatus("signedIn");
    setIsDeviceSession(false);
  }, []);

  const signOut = useCallback<AuthState["signOut"]>(async () => {
    // ⚠ `disablePush()` is NOT called here. It lives inside `teardownAccount`,
    // after the credentials have been collected — unsubscribing first wiped
    // the endpoint this teardown exists to hand back.
    // ⚠ CYCLE 418: the WHOLE teardown, in one place. This used to be
    // `authApi.logout()` + `clearAll()` + `markSignedOut()`, which discarded
    // credentials and left the Live-Activity registration, the widget
    // snapshot and the account-scoped caches behind. `teardownAccount` owns
    // the ordering, the release call, the cycle-358 marker, and the fact
    // that none of it may block on a network that is not there.
    await teardownAccount({ explicit: true });
    // Cycle 36: kill any in-flight alarm preview so the next user
    // doesn't inherit a half-played synthetic alarm overlay.
    cancelPreview();
    setUser(null);
    setStatus("signedOut");
    setIsDeviceSession(false);
  }, []);

  const refreshUser = useCallback(async () => {
    const r = await authApi.me();
    if (r.ok) setUser(r.data);
  }, []);

  return {
    status,
    user,
    signIn,
    signUp,
    adoptSession,
    signOut,
    refreshUser,
    isDeviceSession,
  };
}
